/**
 * admin-map-controls.js
 */

import React from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { TrackContext } from '../track-context';
import { MODE, FILTERING_ALGO, AdminMapContext } from './admin-map-context';


export default function AdminMapControls (props) {
  const { tracks } = React.useContext(TrackContext);
  const {
    mode,
    displayedPointProperty,
    setDisplayedPointProperty,
    setFilteringProperty,
    filtering: {
      algo,
      accuracyFilter,
      calculatedSpeedFilter,
      movingAvgSize,
      movingAvgDev,
      peuckerThreshold,
      kalmanAlgo
    }
  } = React.useContext(AdminMapContext);


  return (
    <>
      {/* property select */}
      <FormControl size='small' sx={{ mb: 3 }}>
        <InputLabel id='detail-type-label' shrink>Track Overlay</InputLabel>
        <Select
          labelId='detail-type-label'
          id='detail-type-select'
          label='Track Overlay'
          displayEmpty
          value={displayedPointProperty}
          onChange={(e, v) => {
            setDisplayedPointProperty(v.props.value);
          }}
        >
          <MenuItem value=''>None</MenuItem>
          <MenuItem value='accuracy'>Accuracy</MenuItem>
          <MenuItem value='speed'>Speed</MenuItem>
          <MenuItem value='speedCalculated'>Calculated Speed</MenuItem>
          <MenuItem value='elapsedTime'>Elapsed Time</MenuItem>
          <MenuItem value='distanceYards'>Distance (Yards)</MenuItem>
        </Select>
      </FormControl>

      {mode === MODE.FILTER &&
      <Typography variant='caption' sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
        <span>Filtering</span>
        <span>Points: {tracks?.[0]?.points?.length}{algo !== '' && <> &rarr; {tracks?.[1]?.points?.length}</>}</span>
      </Typography>}

      {/* filter type select */}
      {mode === MODE.FILTER && <FormControl size='small' sx={{ mb: 2 }} >
        <InputLabel id='filter-type-label' shrink>Algorithm</InputLabel>
        <Select
          labelId='filter-type-label'
          id='filter-type-select'
          label='Algorithm'
          displayEmpty
          value={algo}
          onChange={(e, v) => {
            setFilteringProperty('algo', v.props.value);
          }}
        >
          <MenuItem value=''>None</MenuItem>
          <MenuItem value='default'>Default</MenuItem>
          <MenuItem value='custom'>Custom</MenuItem>
        </Select>
      </FormControl>}

      {algo === FILTERING_ALGO.CUSTOM &&
      <>
        {/* accuracy filter */}
        <Tooltip arrow placement='right' title='Filters out points with accuracy higher than value'>
          <TextField
            type='number'
            size='small'
            label='Accuracy Filter'
            variant='outlined'
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            value={accuracyFilter}
            onChange={(e) => setFilteringProperty('accuracyFilter', e.target.value)}
          />
        </Tooltip>

        {/* calculated speed filter */}
        <Tooltip arrow placement='right' title='Filters out points with calculated speed higher than value'>
          <TextField
            type='number'
            size='small'
            label='Calculated Speed Filter'
            variant='outlined'
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            value={calculatedSpeedFilter}
            onChange={(e) => setFilteringProperty('calculatedSpeedFilter', e.target.value)}
          />
        </Tooltip>

        {/* moving avg accuracy filter */}
        <Tooltip arrow placement='right' title={`Moving average accuracy filter. Filters out points with accuracy higher than moving average + 'Window Accuracy', using the previous 'Window Size' points to calculate the moving average.`}>
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'row' }}>
            <TextField
              type='number'
              size='small'
              label='Window Size'
              variant='outlined'
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderTopRightRadius: 0, borderBottomRightRadius: 0 } }}
              value={movingAvgSize}
              onChange={(e) => setFilteringProperty('movingAvgSize', e.target.value)}
            />
            <TextField
              type='number'
              size='small'
              label='Window Accuracy'
              variant='outlined'
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, ml: '-1px', '& .MuiOutlinedInput-root': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
              value={movingAvgDev}
              onChange={(e) => setFilteringProperty('movingAvgDev', e.target.value)}
            />
          </Box>
        </Tooltip>

        {/* peucker threshold */}
        <Tooltip arrow placement='right' title='Filters out points unnecessary for maintaining the track shape, based on a given threshold. Larger values simplify the shape and discard more points.'>
          <TextField
            type='number'
            size='small'
            label='Peucker Threshold'
            variant='outlined'
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            value={peuckerThreshold}
            onChange={(e) => setFilteringProperty('peuckerThreshold', e.target.value)}
          />
        </Tooltip>

        {/* kalman select */}
        <Tooltip arrow placement='right' title='Uses a smoothing algorithm to remove angles. Not recommended as it seems to reduce valid accuracy.'>
          <FormControl size='small' sx={{ mb: 2 }}>
            <InputLabel id='kalman-select-label' shrink>Kalman Algo</InputLabel>
            <Select
              labelId='kalman-select-label'
              id='kalman-select'
              label='Kalman Algo'
              displayEmpty
              value={kalmanAlgo}
              onChange={(e, v) => setFilteringProperty('kalmanAlgo', v.props.value)}
            >
              <MenuItem value=''>None</MenuItem>
              <MenuItem value='2ds'>2D Smoothing</MenuItem>
              <MenuItem value='2dcp'>2D Constant Position</MenuItem>
              <MenuItem value='2dca'>2D Constant Acceleration</MenuItem>
            </Select>
          </FormControl>
        </Tooltip>
      </>}
    </>
  );
}